import {useAccessToken} from "../../services/auth"
import React, {useEffect, useState} from "react"
import {
	deleteDomain,
	DnsSetting,
	getClientDomainConfig,
	OrgSettings,
	setupDomain,
	VerifyDomain,
} from "../../resources/orgSettings"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {ErrorDocument} from "../../resources/common"
import {faCircleNotch} from "@fortawesome/free-solid-svg-icons"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import classNames from "classnames"
import {useModal} from "react-modal-hook"
import SetupDomainSettingsModal from "../../components/OrgAndBankSettings/SetupDomainSettingsModal"
import Admonition from "../../components/Admonitions/Admonition"
import DeleteDomainSettingsModal from "../../components/OrgAndBankSettings/DeleteDomainSettingsModal"

function ClientDomainSetup({refresh, orgSettings}: {refresh: () => void; orgSettings: OrgSettings}) {
	const [showSetupModal, hideSetupModal] = useModal(() => (
		<SetupDomainSettingsModal
			close={hideSetupModal}
			refresh={refresh}
			setupDomain={setupDomain}
			domainExampleText="Example: 'apply.yourdomain.com'"
		>
			<Admonition
				type="is-info"
				title="Note"
				message={
					<>
						To complete the subdomain configuration, you have 72 hours to add the records to you DNS provider. Learn
						more{" "}
						<a
							className="link"
							href="https://docs.unit.co/application-forms/#customizing-the-application-form-domain"
							rel="noreferrer"
							target="_blank"
						>
							here
						</a>
						.
					</>
				}
			/>
		</SetupDomainSettingsModal>
	))

	return (
		<div className="field ">
			<label className="label">Domain Name</label>
			{!orgSettings.attributes.applicationFormDomainSettings.applicationFormDomain ? (
				<p className="field has-text-grey">Click setup to configure a URL with your own subdomain</p>
			) : null}
			<div className="field has-addons is-horizontal">
				{orgSettings.attributes.applicationFormDomainSettings.applicationFormDomain ? (
					<div className="control is-expanded">
						<input
							className="input"
							required
							type="text"
							value={orgSettings.attributes.applicationFormDomainSettings.applicationFormDomain ?? ""}
							placeholder="A subdomain you own"
							disabled
						/>
					</div>
				) : null}
				<button
					type="button"
					className={classNames("button", "is-success")}
					onClick={(e) => {
						e.preventDefault()
						showSetupModal()
					}}
				>
					Setup
				</button>
			</div>
		</div>
	)
}

function ClientDomainConfigInternal({
	dnsSettings,
	refresh,
	orgSettings,
}: {
	dnsSettings: Array<DnsSetting>
	refresh: () => void
	orgSettings: OrgSettings
}) {
	const accessToken = useAccessToken()
	const status = orgSettings.attributes.applicationFormDomainSettings.applicationFormCertificateStatus
	const [isLoading, setIsLoading] = useState(false)
	const [verifyDomainState, verifyDomain] = useAsyncResultIdle(VerifyDomain)

	useEffect(() => {
		verifyDomainState.match(
			() => setIsLoading(false),
			() => setIsLoading(true),
			() => {
				setIsLoading(false)
				refresh()
			},
			() => setIsLoading(false)
		)
	}, [verifyDomainState])

	const [showEditModal, hideEditModal] = useModal(() => (
		<DeleteDomainSettingsModal
			domainName={orgSettings.attributes.applicationFormDomainSettings.applicationFormDomain ?? ""}
			close={hideEditModal}
			refresh={refresh}
			deleteDomain={deleteDomain}
		/>
	))

	return (
		<div className="field">
			<div className="box">
				<span className="has-text-weight-bold">
					DNS Settings -{" "}
					<span className={status == "Verified" ? "has-text-success" : "has-text-warning"}>
						{orgSettings.attributes.applicationFormDomainSettings.applicationFormCertificateStatus}
					</span>
				</span>
				<p className="mt-2 mb-2">
					Please config these records in your DNS provider within 72 hours to verify the domain ownership.
				</p>
				<table className="table is-fullwidth">
					<thead>
						<tr>
							<td>Record Name</td>
							<td>Type</td>
							<td>Record Name</td>
						</tr>
					</thead>
					<tbody>
						{dnsSettings.map((d) => (
							<tr key={d.attributes.key}>
								<td>{d.attributes.key}</td>
								<td>CNAME</td>
								<td>{d.attributes.value}</td>
							</tr>
						))}
					</tbody>
				</table>
				{status == "Unverified" ? (
					<button
						className="button is-success"
						disabled={isLoading}
						onClick={(e) => {
							e.preventDefault()
							setIsLoading(true)
							verifyDomain(accessToken)
						}}
					>
						{isLoading ? <FontAwesomeIcon icon={faCircleNotch} spin /> : "Verify"}
					</button>
				) : null}
				<button
					type="button"
					className={classNames("button", "is-danger")}
					onClick={(e) => {
						e.preventDefault()
						showEditModal()
					}}
				>
					Delete
				</button>
				{verifyDomainState.isErr() ? (
					<p className="has-text-danger mt-2">
						{" "}
						{verifyDomainState.error.errors[0].title} - {verifyDomainState.error.errors[0].detail}{" "}
					</p>
				) : null}
			</div>
		</div>
	)
}

function ClientDomainConfigError({retry}: {retry: () => void}) {
	return (
		<div className="field">
			<div className="box">
				<span className="has-text-weight-bold">
					DNS Settings - <span className="has-text-warning">Failed to retrieve data</span>
				</span>
				<p className="mt-2 mb-2">Please try again</p>

				<button
					type="button"
					className="button is-success"
					onClick={(e) => {
						e.preventDefault()
						retry()
					}}
				>
					Retry
				</button>
			</div>
		</div>
	)
}

function ClientDomainConfigPending() {
	return (
		<div className="field">
			<div className="box">
				<span className="has-text-weight-bold">
					DNS Settings -{" "}
					<span>
						<FontAwesomeIcon className="card-icon" icon={faCircleNotch} spin />
					</span>
				</span>
			</div>
		</div>
	)
}

export default function ClientDomainConfig({refresh, orgSettings}: {orgSettings: OrgSettings; refresh: () => void}) {
	if (!orgSettings.attributes.applicationFormDomainSettings.applicationFormDomain) {
		return <ClientDomainSetup refresh={refresh} orgSettings={orgSettings} />
	}

	const accessToken = useAccessToken()

	const [retryToken, retry] = useRefreshToken()
	const dnsSettingsAsync = useAsyncResult<Array<DnsSetting>, ErrorDocument>(
		() => getClientDomainConfig(accessToken),
		[retryToken]
	)

	return dnsSettingsAsync.match(
		() => <ClientDomainConfigPending />,
		(dnsSettings) => (
			<ClientDomainConfigInternal refresh={refresh} dnsSettings={dnsSettings} orgSettings={orgSettings} />
		),
		(_) => <ClientDomainConfigError retry={retry} />
	)
}
